/**
 * THE BETA MARK. One quiet, permanent statement that this game is unfinished.
 *
 * Alex, 2026-08-15, in the same breath as opening the doors: *"Let's remove the wallet blocker for
 * revival and it is now in open beta to anyone who wants to enter"* / *"We do need a comment that it
 * is in beta mode."*
 *
 * ⭐ **THE TWO INSTRUCTIONS ARE ONE INSTRUCTION.** While the beta was CLOSED, everybody who could
 * reach the game had been handed the URL personally and already knew what they were playing - the
 * whitelist itself carried the context. Opening it deletes that channel: from now on a visitor
 * arrives from a link with no idea whether they are looking at a finished product. The mark is what
 * replaces the invitation.
 *
 * ⚠️ **IT SAYS WHAT BETA COSTS THEM, NOT JUST THAT IT IS BETA.** A bare "BETA" badge is decoration;
 * every product wears one and no player reads it. The thing a player actually needs to know is that
 * their progress may be reset and their bugs are worth reporting - so the note says exactly that,
 * and the reporting route is the same in-page bug reporter testers already use.
 *
 * ⛔ **IT LIVES ON `document.body`, NOT ON THE STAGE.** `app/stage.ts` scales the whole game as one
 * object to fit the viewport; anything mounted inside it is scaled with it, so a badge there would
 * shrink on a small screen - the one screen where it is least likely to be read. It is also
 * `pointer-events` transparent apart from its own button, because it sits over a game board and a
 * mark that eats a click on a card would be a real defect, not a cosmetic one.
 */

const MARK_ID = "betamark";

/** Mount the beta mark once. Safe to call repeatedly - a second call is a no-op. */
export function mountBetaMark(): void {
  if (document.getElementById(MARK_ID)) return;

  const root = document.createElement("div");
  root.id = MARK_ID;
  root.className = "betamark";

  const badge = document.createElement("button");
  badge.type = "button";
  badge.className = "betamark__badge";
  badge.textContent = "OPEN BETA";
  // ⚠️ The accessible name carries the whole sentence. A screen reader announcing the word "beta"
  // alone tells its user strictly less than the sighted player gets from the panel below.
  badge.setAttribute("aria-label", "Revival is in open beta. What that means.");
  badge.setAttribute("aria-expanded", "false");

  const note = document.createElement("div");
  note.className = "betamark__note";
  note.hidden = true;
  note.textContent =
    "Revival is playable and unfinished. Cards, decks and campaign progress may be reset while we "
    + "build, and you may hit a bug. Telling us about one is the single most useful thing you can "
    + "do here.";

  badge.addEventListener("click", () => {
    const open = note.hidden;
    note.hidden = !open;
    badge.setAttribute("aria-expanded", open ? "true" : "false");
    root.classList.toggle("betamark--open", open);
  });

  root.append(badge, note);
  document.body.append(root);
}
